import React from 'react';
import { motion } from 'framer-motion';
import { Clock, Store, ArrowRight } from 'lucide-react';
export function Slide02Opportunity() {
  return <div className="w-full min-h-full flex flex-col justify-center p-[clamp(1.5rem,6vw,6rem)]" data-id="element-65">
      <div className="max-w-6xl mx-auto w-full flex flex-col lg:grid lg:grid-cols-2 gap-[clamp(1.5rem,6vw,6rem)] items-center" data-id="element-66">
        {/* Left: Content */}
        <motion.div initial={{
        opacity: 0,
        x: -30
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} data-id="element-67">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-surface border border-surface-hover text-accent text-xs md:text-sm font-bold tracking-wide uppercase mb-4 md:mb-8" data-id="element-68">
            <Clock size={16} data-id="element-69" />
            The Opportunity
          </div>

          <h2 className="text-[clamp(2rem,5vw,4.5rem)] font-bold leading-tight mb-4 md:mb-6" data-id="element-70">
            3 to 5 minutes of{' '}
            <span className="text-accent text-glow" data-id="element-71">opportunity!</span>
          </h2>

          <p className="text-[clamp(1rem,2vw,1.5rem)] text-muted leading-relaxed text-balance mb-6 md:mb-8" data-id="element-72">
            Every day, hundreds of patrons stand at your gas pumps with phone in
            hand and time to spare. It's a captive, ready-to-engage audience —
            and the perfect moment to invite them inside the casino and travel
            center.
          </p>

          <div className="flex items-center gap-4 text-accent-secondary font-medium text-base md:text-lg" data-id="element-73">
            <span data-id="element-74">Let's make every minute count</span>
            <ArrowRight size={20} data-id="element-75" />
          </div>
        </motion.div>

        {/* Right: Visual */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="relative aspect-square max-h-[30vh] lg:max-h-[60vh] w-full shrink-0" data-id="element-76">
          {/* Low-fi scene: interstate traffic pulling into a gas station */}
          <div className="absolute inset-0 bg-surface rounded-3xl border border-surface-hover overflow-hidden flex flex-col" data-id="element-77">
            <div className="flex-1 relative" data-id="element-78">
              <svg viewBox="0 0 400 400" className="w-full h-full" role="img" aria-label="Low-fidelity illustration of cars on an interstate pulling into a gas station" data-id="element-79">
                {/* Ground */}
                <rect x="0" y="0" width="400" height="400" fill="#0f172a" data-id="element-80" />
                {/* Grass / median */}
                <rect x="0" y="250" width="400" height="150" fill="#0b1220" data-id="element-81" />

                {/* Interstate road */}
                <rect x="0" y="150" width="400" height="100" fill="#1e293b" data-id="element-82" />
                <rect x="0" y="150" width="400" height="4" fill="#334155" data-id="element-83" />
                <rect x="0" y="246" width="400" height="4" fill="#334155" data-id="element-84" />
                {/* Scrolling lane dashes */}
                <motion.g animate={{
                x: [0, -60]
              }} transition={{
                duration: 1.2,
                repeat: Infinity,
                ease: 'linear'
              }} data-id="element-85">
                  {Array.from({
                  length: 10
                }).map((_, i) => <rect key={i} x={i * 60} y="198" width="34" height="5" rx="2.5" fill="#3b9eff" opacity="0.8" data-id="element-86" />)}
                </motion.g>

                {/* Highway shield sign */}
                <g transform="translate(28 60)" data-id="element-87">
                  <rect width="56" height="60" rx="10" fill="#0f172a" stroke="#334155" strokeWidth="3" data-id="element-88" />
                  <text x="28" y="26" textAnchor="middle" fontFamily="Outfit, sans-serif" fontWeight="800" fontSize="11" fill="#94a3b8" data-id="element-89">
                    INTERSTATE
                  </text>
                  <text x="28" y="50" textAnchor="middle" fontFamily="Outfit, sans-serif" fontWeight="900" fontSize="24" fill="#3bf65d" data-id="element-90">
                    40
                  </text>
                </g>

                {/* Gas station canopy */}
                <g transform="translate(232 70)" data-id="element-91">
                  {/* Posts */}
                  <rect x="14" y="44" width="8" height="86" fill="#334155" data-id="element-92" />
                  <rect x="118" y="44" width="8" height="86" fill="#334155" data-id="element-93" />
                  {/* Roof */}
                  <rect x="-6" y="22" width="152" height="26" rx="6" fill="#3b9eff" data-id="element-94" />
                  <rect x="-6" y="44" width="152" height="6" fill="#0b1220" opacity="0.3" data-id="element-95" />
                  {/* Sign */}
                  <rect x="40" y="0" width="60" height="22" rx="5" fill="#3bf65d" data-id="element-96" />
                  <text x="70" y="15" textAnchor="middle" fontFamily="Outfit, sans-serif" fontWeight="800" fontSize="11" fill="#022c12" data-id="element-97">
                    FUEL
                  </text>
                  {/* Pumps */}
                  <rect x="36" y="92" width="14" height="32" rx="3" fill="#475569" data-id="element-98" />
                  <rect x="90" y="92" width="14" height="32" rx="3" fill="#475569" data-id="element-99" />
                  <rect x="38" y="96" width="10" height="8" rx="1.5" fill="#3bf65d" data-id="element-100" />
                  <rect x="92" y="96" width="10" height="8" rx="1.5" fill="#3bf65d" data-id="element-101" />
                </g>

                {/* Travel Center building */}
                <g transform="translate(232 210)" data-id="element-102">
                  <rect x="0" y="0" width="150" height="40" rx="6" fill="#162236" stroke="#334155" strokeWidth="2" data-id="element-103" />
                  <text x="75" y="25" textAnchor="middle" fontFamily="Outfit, sans-serif" fontWeight="800" fontSize="13" fill="#94a3b8" letterSpacing="1" data-id="element-104">
                    TRAVEL CENTER
                  </text>
                </g>

                {/* Car parked at the pump */}
                <g transform="translate(250 150)" data-id="element-105">
                  <rect x="0" y="0" width="58" height="22" rx="9" fill="#3bf65d" data-id="element-106" />
                  <rect x="12" y="-9" width="34" height="14" rx="6" fill="#86efac" data-id="element-107" />
                  <circle cx="14" cy="24" r="6" fill="#0b1220" stroke="#475569" strokeWidth="2" data-id="element-108" />
                  <circle cx="44" cy="24" r="6" fill="#0b1220" stroke="#475569" strokeWidth="2" data-id="element-109" />
                </g>

                {/* Car driving along the interstate (loops across) */}
                <motion.g animate={{
                x: [-80, 460]
              }} transition={{
                duration: 4.5,
                repeat: Infinity,
                ease: 'linear'
              }} data-id="element-110">
                  <g transform="translate(0 182)" data-id="element-111">
                    <rect x="0" y="0" width="64" height="24" rx="10" fill="#3b9eff" data-id="element-112" />
                    <rect x="14" y="-10" width="36" height="15" rx="6" fill="#60a5fa" data-id="element-113" />
                    <circle cx="16" cy="26" r="6.5" fill="#0b1220" stroke="#475569" strokeWidth="2" data-id="element-114" />
                    <circle cx="48" cy="26" r="6.5" fill="#0b1220" stroke="#475569" strokeWidth="2" data-id="element-115" />
                  </g>
                </motion.g>
              </svg>
            </div>

            <div className="px-8 py-6 border-t border-surface-hover flex items-center justify-center gap-3 text-accent" data-id="element-116">
              <Store size={22} data-id="element-117" />
              <span className="font-medium" data-id="element-118">
                A captive audience, every fill-up
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>;
}